import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Icon,
  Theme,
  Typography,
} from "@mui/material";
import { radius } from "../../../../theme";
import FelixAnalytics from "../../../../felixAnalytics";
import { Link } from "react-router-dom";
import ChipNegociacao from "../../../util/ui/chipNegociacao";
import { SituacaoNota } from "../../../../models/enum";
import { NotaBMF, NotaBovespa } from "../../../../models/nota";
import {
  IconFileAlert,
  IconFileCheck,
  IconFileSearch,
  IconFileX,
  IconTrashX,
} from "@tabler/icons-react";
import { deleteDoc } from "firebase/firestore";
import NotaDb from "../../../../db/notaDb";

type NotaCardProps = {
  nota: NotaBovespa | NotaBMF;
};

/* Estado da nota → uma das cinco semânticas de `palette.statusChip`:
   parcial/obsoleta = warn, inconsistente = destructive. */
const sxChipWarn = (theme: Theme) => ({
  backgroundColor: theme.palette.statusChip.warn.bg,
  color: theme.palette.statusChip.warn.fg,
});

const sxChipDestrutivo = (theme: Theme) => ({
  backgroundColor: theme.palette.statusChip.destructive.bg,
  color: theme.palette.statusChip.destructive.fg,
});

export function NotaCard({ nota }: NotaCardProps): JSX.Element {
  return (
    <Card
      sx={{
        width: "26ch",
        borderRadius: `${radius.lg}px`,
      }}
      variant="outlined"
    >
      <CardHeader
        sx={{ paddingBottom: 0.5 }}
        title={
          <Box display="flex" flexDirection="row">
            <Box display="flex" flexDirection="row" flexGrow={1}>
              <ChipNegociacao negociacao={nota.tipoNegociacao} />
              <Box paddingX={0.5} />
              {(function () {
                switch (nota.situacao) {
                  case SituacaoNota.Parcial:
                    return (
                      <Chip size="small" label="Parcial" sx={sxChipWarn} />
                    );
                  case SituacaoNota.Obsoleta:
                    return (
                      <Chip size="small" label="Obsoleta" sx={sxChipWarn} />
                    );
                  case SituacaoNota.Inconsistente:
                    return (
                      <Chip
                        size="small"
                        label="Inconsistente"
                        sx={sxChipDestrutivo}
                      />
                    );
                  default:
                    return <></>;
                }
              })()}
            </Box>
            <Box alignSelf={"center"}>
              <Typography variant="dataMono" color="text.secondary">
                {nota.dataPregao.format("DD/MM/YY")}
              </Typography>
            </Box>
          </Box>
        }
      />
      <CardContent sx={{ paddingBottom: 0.5 }}>
        {nota.erro && (
          <Typography
            variant="dataMono"
            color="text.secondary"
            sx={{ fontSize: 12, wordBreak: "break-word" }}
          >
            {(function () {
              switch (nota.erro.codErro) {
                default:
                  return `${nota.erro.erro} > ${
                    nota.erro.detalhes
                      ? nota.erro.detalhes
                      : `codErro: ${nota.erro.codErro}`
                  }`;
              }
            })()}
          </Typography>
        )}
      </CardContent>
      <CardActions>
        <Box display={"flex"} flexGrow={1}>
          {(function () {
            switch (nota.situacao) {
              case SituacaoNota.Pronta:
                return (
                  <Icon fontSize="small" color="success">
                    <IconFileCheck size={20} />
                  </Icon>
                );
              case SituacaoNota.Parcial:
              case SituacaoNota.Obsoleta:
                return (
                  <Icon fontSize="small" color="warning">
                    <IconFileAlert size={20} />
                  </Icon>
                );
              case SituacaoNota.Inconsistente:
                return (
                  <Icon fontSize="small" color="error">
                    <IconFileX size={20} />
                  </Icon>
                );
              default:
                return <></>;
            }
          })()}
        </Box>
        <Box display={"flex"} flexDirection={"row"}>
          <ButtonGroup size="small" variant="outlined">
            {nota.situacao === SituacaoNota.Pronta && (
              <Button
                size="small"
                color="primary"
                component={Link}
                to={`/arquivo/nota/${nota.id}`}
                onClick={() => {
                  FelixAnalytics.track("ver-nota", {
                    nota: nota.id,
                  });
                }}
              >
                <IconFileSearch size={16} />
              </Button>
            )}
            <Button
              size="small"
              color="error"
              onClick={() =>
                deleteDoc(
                  new NotaDb().doc({ usuarioId: nota.usuario, notaId: nota.id })
                )
              }
            >
              <IconTrashX size={16} />
            </Button>
          </ButtonGroup>
        </Box>
      </CardActions>
    </Card>
  );
}
